import { useEffect, useSyncExternalStore } from "react";
import { useWorldT } from "../i18n.js";
import { floorRows, roomLayout } from "../interior/layout.js";
import type { FloorRow } from "../interior/layout.js";
import { useNavSelector, navStore } from "../camera/nav.js";
import type { NavStore } from "../camera/nav.js";
import { useWorldState } from "../store.js";
import type { WorldStore } from "../store.js";
import { elevators } from "./store.js";
import type { ElevatorStore } from "./store.js";

/**
 * The elevator's controls on screen: a prompt when a call plate or a cabin panel is within reach
 * (the E key, or a tap on it), then the panel: Up and Down at a call plate, the floors by name
 * (and the door buttons) in the cabin. A tap on the plate or the panel in the world opens it too.
 */
export function ElevatorHud({ store, nav = navStore }: { store: WorldStore; nav?: NavStore }) {
  const insideId = useNavSelector((s) => s.inside?.id ?? null, nav);
  const lift = insideId ? elevators.get(insideId) : undefined;
  if (!lift) return null;
  return <Panel key={insideId} lift={lift} store={store} insideId={insideId!} nav={nav} />;
}

function Panel({
  lift,
  store,
  insideId,
  nav,
}: {
  lift: ElevatorStore;
  store: WorldStore;
  insideId: string;
  nav: NavStore;
}) {
  const t = useWorldT();
  useSyncExternalStore(lift.subscribe, lift.getVersion);
  const world = useWorldState(store);
  const node = store.node(insideId);
  const building =
    world.org?.companies.flatMap((c) => c.buildings).find((b) => b.id === insideId) ?? null;
  const rows: FloorRow[] =
    node && node.geometry.kind === "procedural"
      ? floorRows(roomLayout(node.geometry.params), building)
      : [];
  const names = new Map(rows.map((r) => [r.level, r.name]));
  const panel = lift.panel;
  const near = lift.near;
  useEffect(() => {
    if (!panel) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.code === "Escape") lift.closePanel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lift, panel]);
  void nav;
  const cabinName = (i: number) => (lift.slots.length > 1 ? `${"AB"[i]}` : "");
  if (panel) {
    const cab = lift.sim.cabins[panel.cabin]!;
    return (
      <section
        className="world-lift"
        data-testid="lift-panel"
        data-kind={panel.kind}
        aria-label={t("interior.elevator")}
      >
        <header>
          <strong>{t("interior.liftCar", { name: cabinName(panel.cabin) }).trim()}</strong>
          <button
            type="button"
            data-testid="lift-close"
            aria-label={t("interior.liftClosePanel")}
            onClick={() => lift.closePanel()}
          >
            ×
          </button>
        </header>
        {panel.kind === "hall" ? (
          <div className="world-lift-call">
            <button
              type="button"
              data-testid="lift-call-up"
              aria-pressed={lift.sim.up[lift.drawn] === true}
              disabled={lift.drawn === lift.levels.length - 1}
              onClick={() => lift.callHall(1)}
            >
              ▲ {t("interior.liftUp")}
            </button>
            <button
              type="button"
              data-testid="lift-call-down"
              aria-pressed={lift.sim.down[lift.drawn] === true}
              disabled={lift.drawn === 0}
              onClick={() => lift.callHall(-1)}
            >
              ▼ {t("interior.liftDown")}
            </button>
          </div>
        ) : (
          <>
            <ul>
              {[...lift.levels.keys()].reverse().map((i) => {
                const level = lift.levels[i]!;
                const name = names.get(level);
                return (
                  <li key={level}>
                    <button
                      type="button"
                      data-testid={`lift-floor-${level}`}
                      aria-pressed={cab.car[i] === true}
                      aria-current={cab.at === i && cab.phase !== "moving" ? "true" : undefined}
                      title={cab.at === i ? t("interior.liftHere") : undefined}
                      onClick={() => lift.pressFloor(panel.cabin, i)}
                    >
                      <strong>{level}</strong>
                      <span>{name ?? t("interior.floorTitle", { level })}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="world-lift-doors">
              <button
                type="button"
                data-testid="lift-door-open"
                onClick={() => lift.pressDoor(panel.cabin, "open")}
              >
                ◀▶ {t("interior.liftOpen")}
              </button>
              <button
                type="button"
                data-testid="lift-door-close"
                onClick={() => lift.pressDoor(panel.cabin, "close")}
              >
                ▶◀ {t("interior.liftClose")}
              </button>
            </div>
          </>
        )}
      </section>
    );
  }
  if (!near) return null;
  return (
    <button
      type="button"
      className="world-prompt"
      data-testid="lift-use"
      onClick={() => lift.openPanel()}
    >
      {near.kind === "hall" ? t("interior.liftCall") : t("interior.liftPick")} <kbd>E</kbd>
    </button>
  );
}
